// src/components/Auth/TermsAndConditions.js
import React from 'react';
import './TermsAndConditions.css';

const TermsAndConditions = () => {
  return (
    <div className="terms-content">
      <div className="terms-meta">
        <p><strong>Effective Date:</strong> 01-01-2025</p>
        <p><strong>Last Updated:</strong> 01-01-2026</p>
      </div>

      <section>
        <h3>1. INTRODUCTION & ACCEPTANCE OF TERMS</h3>
        <p>Welcome to TREXAR ("TREXAR", "we", "us", "our", or the "Platform").</p>
        <p>These Terms and Conditions ("Terms") govern your access to and use of:</p>
        <ul>
          <li>The TREXAR mobile application</li>
          <li>The TREXAR website</li>
          <li>All products, features, tools, and services offered by TREXAR (collectively, the "Services")</li>
        </ul>
        <p>By accessing, registering, or using TREXAR, you confirm that:</p>
        <ul>
          <li>You have read and understood these Terms</li>
          <li>You agree to be legally bound by them</li>
          <li>You understand the risks associated with investments on the Platform</li>
        </ul>
        <p><strong>If you do not agree to these Terms, you must not use TREXAR.</strong></p>
      </section>

      <section>
        <h3>2. DEFINITIONS</h3>
        <p>For the purposes of these Terms:</p>
        <ul>
          <li><strong>"User"</strong> means any person who accesses or uses TREXAR</li>
          <li><strong>"Investor"</strong> means a User who invests in assets on the Platform</li>
          <li><strong>"Assets"</strong> means investment opportunities listed on TREXAR</li>
          <li><strong>"SPV"</strong> means Special Purpose Vehicle</li>
          <li><strong>"Secondary Market"</strong> means the internal marketplace where eligible assets may be traded</li>
          <li><strong>"Auto-Invest Wallet"</strong> refers to pooled investment features, not savings</li>
          <li><strong>"Business"</strong> refers to the underlying venture or company associated with an asset</li>
        </ul>
      </section>

      <section>
        <h3>3. ELIGIBILITY TO USE TREXAR</h3>
        <p>To use TREXAR, you must:</p>
        <ol>
          <li>Be at least 18 years old</li>
          <li>Have the legal capacity to enter binding contracts</li>
          <li>Provide accurate, current, and complete information</li>
          <li>Comply with all applicable Nigerian laws and regulations</li>
          <li>Not be prohibited from using financial or investment platforms</li>
        </ol>
        <p>TREXAR reserves the right to refuse access or terminate accounts at its sole discretion.</p>
      </section>

      <section>
        <h3>4. NATURE OF TREXAR</h3>
        <h4>4.1 TREXAR IS NOT A BANK</h4>
        <p>TREXAR:</p>
        <ul>
          <li>Is not a bank</li>
          <li>Is not a microfinance institution</li>
          <li>Does not accept deposits</li>
          <li>Does not offer fixed or guaranteed interest</li>
        </ul>
        <p>Funds placed on TREXAR are not protected by NDIC or any deposit insurance scheme.</p>

        <h4>4.2 TREXAR IS AN INVESTMENT PLATFORM</h4>
        <p>TREXAR provides users with access to private investment opportunities, including but not limited to:</p>
        <ul>
          <li>Nigerian private businesses</li>
          <li>Growth and expansion rounds</li>
          <li>Late-stage ventures</li>
          <li>Private equity investments</li>
        </ul>
        <p>All investments are subject to risk.</p>
      </section>

      <section>
        <h3>5. ACCOUNT REGISTRATION & SECURITY</h3>
        <h4>5.1 Account Creation</h4>
        <p>You must create an account to access investment features. During registration, you agree to provide accurate information.</p>

        <h4>5.2 Account Responsibility</h4>
        <p>You are solely responsible for:</p>
        <ul>
          <li>Safeguarding your login credentials</li>
          <li>All activities conducted under your account</li>
        </ul>
        <p>TREXAR will not be liable for unauthorized access caused by your negligence.</p>
      </section>

      <section>
        <h3>6. KYC, AML & COMPLIANCE</h3>
        <p>TREXAR complies with applicable KYC (Know Your Customer) and AML (Anti-Money Laundering) regulations.</p>
        <p>We may require:</p>
        <ul>
          <li>Government-issued identification</li>
          <li>Proof of address</li>
          <li>Bank verification</li>
          <li>Additional documents as required by law</li>
        </ul>
        <p>Failure to comply may result in:</p>
        <ul>
          <li>Restricted access</li>
          <li>Account suspension</li>
          <li>Termination</li>
        </ul>
      </section>

      <section>
        <h3>7. INVESTMENT STRUCTURE & SPVs</h3>
        <h4>7.1 SPV-Based Investments</h4>
        <p>Most assets on TREXAR are structured using Special Purpose Vehicles (SPVs).</p>
        <p>When you invest:</p>
        <ul>
          <li>You invest in an SPV</li>
          <li>The SPV holds an interest in the underlying business</li>
          <li>You do NOT directly own the business</li>
        </ul>

        <h4>7.2 Asset Information</h4>
        <p>All asset data, including:</p>
        <ul>
          <li>Valuations</li>
          <li>Projected returns</li>
          <li>Financial metrics</li>
        </ul>
        <p>are estimates, provided for informational purposes only.</p>
      </section>

      <section>
        <h3>8. RISK DISCLOSURE</h3>
        <p>By using TREXAR, you expressly acknowledge and accept that:</p>
        <ol>
          <li>Investments may be illiquid</li>
          <li>Capital is at risk</li>
          <li>Returns are not guaranteed</li>
          <li>Business failures may occur</li>
          <li>Market, regulatory, and operational risks exist</li>
          <li>Past performance does not predict future results</li>
        </ol>
        <p>You are solely responsible for evaluating investment suitability.</p>
      </section>

      <section>
        <h3>9. ASSET CATEGORIES & RISK LEVELS</h3>
        <p>TREXAR may categorize assets into groups such as:</p>
        <ul>
          <li>Core Nigerian Assets</li>
          <li>Growth Rounds</li>
          <li>Scale-Up Ventures</li>
          <li>Private Equity</li>
        </ul>
        <p>Each category carries different risk levels. Higher potential returns generally come with higher risk.</p>
      </section>

      <section>
        <h3>10. SECONDARY MARKET & TRADING</h3>
        <h4>10.1 Availability</h4>
        <p>Only assets explicitly displayed on the core ng assets may be traded on the Secondary Market.</p>

        <h4>10.2 Pricing</h4>
        <p>Prices are determined by:</p>
        <ul>
          <li>Supply and demand</li>
          <li>Asset performance</li>
          <li>Market sentiment</li>
        </ul>
        <p>TREXAR does not guarantee liquidity or pricing.</p>

        <h4>10.3 Platform Control</h4>
        <p>TREXAR reserves the right to:</p>
        <ul>
          <li>Set trading windows</li>
          <li>Suspend trading</li>
          <li>Delist assets</li>
          <li>Cancel or reverse trades in exceptional circumstances</li>
        </ul>
      </section>

      <section>
        <h3>11. AUTO-INVEST WALLET</h3>
        <h4>11.1 Nature of Feature</h4>
        <p>Funds placed in Auto-Invest or Investment Wallets:</p>
        <ul>
          <li>Are NOT savings</li>
          <li>Are NOT deposits</li>
          <li>Are deployed into selected investments</li>
        </ul>

        <h4>11.2 Risk</h4>
        <p>Returns are variable and dependent on asset performance. Capital is at risk.</p>
      </section>

      <section>
        <h3>12. FEES & CHARGES</h3>
        <p>TREXAR may charge:</p>
        <ul>
          <li>Management fees</li>
          <li>Performance fees</li>
          <li>Trading fees</li>
          <li>Transaction or processing fees</li>
        </ul>
        <p>All fees will be disclosed prior to execution.</p>
      </section>

      <section>
        <h3>13. PAYOUTS & WITHDRAWALS</h3>
        <h4>13.1 Profit Distribution</h4>
        <p>Payouts depend on:</p>
        <ul>
          <li>Business performance</li>
          <li>SPV agreements</li>
          <li>Cash flow availability</li>
        </ul>

        <h4>13.2 Delays</h4>
        <p>Delays may occur due to:</p>
        <ul>
          <li>Audits</li>
          <li>Regulatory checks</li>
          <li>Business conditions</li>
        </ul>
        <p>TREXAR is not liable for delays beyond its control.</p>
      </section>

      <section>
        <h3>14. USER CONDUCT & PROHIBITED ACTIVITIES</h3>
        <p>You agree NOT to:</p>
        <ul>
          <li>Manipulate markets</li>
          <li>Spread false information</li>
          <li>Engage in fraud</li>
          <li>Use the Platform for illegal purposes</li>
        </ul>
        <p>Violations may lead to immediate termination.</p>
      </section>

      <section>
        <h3>15. INTELLECTUAL PROPERTY</h3>
        <p>All content, trademarks, software, and branding belong to TREXAR and may not be used without permission.</p>
      </section>

      <section>
        <h3>16. DISCLAIMER OF WARRANTIES</h3>
        <p>TREXAR is provided "as is" and "as available." We make no warranties regarding:</p>
        <ul>
          <li>Performance</li>
          <li>Accuracy</li>
          <li>Availability</li>
          <li>Profitability</li>
        </ul>
      </section>

      <section>
        <h3>17. LIMITATION OF LIABILITY</h3>
        <p>To the fullest extent permitted by law, TREXAR shall not be liable for:</p>
        <ul>
          <li>Investment losses</li>
          <li>Business failures</li>
          <li>Market fluctuations</li>
          <li>Indirect or consequential damages</li>
        </ul>
      </section>

      <section>
        <h3>18. INDEMNIFICATION</h3>
        <p>You agree to indemnify and hold harmless TREXAR, its directors, officers, employees, and partners from claims arising from:</p>
        <ul>
          <li>Your use of the Platform</li>
          <li>Breach of these Terms</li>
          <li>Violation of laws</li>
        </ul>
      </section>

      <section>
        <h3>19. TERMINATION</h3>
        <p>TREXAR may suspend or terminate your account:</p>
        <ul>
          <li>For regulatory reasons</li>
          <li>For breach of these Terms</li>
          <li>For risk management</li>
        </ul>
        <p>Termination does not affect existing obligations.</p>
      </section>

      <section>
        <h3>20. DATA PROTECTION & PRIVACY</h3>
        <p>TREXAR processes personal data in accordance with:</p>
        <ul>
          <li>Nigerian Data Protection Act</li>
          <li>Our Privacy Policy</li>
        </ul>
      </section>

      <section>
        <h3>21. AMENDMENTS</h3>
        <p>TREXAR may modify these Terms at any time. Continued use constitutes acceptance.</p>
      </section>

      <section>
        <h3>22. GOVERNING LAW & JURISDICTION</h3>
        <p>These Terms are governed by the laws of the Federal Republic of Nigeria. Disputes shall be resolved in Nigerian courts.</p>
      </section>

      <section>
        <h3>23. ANTI-FRAUD, REFERRAL & MULTI-ACCOUNT POLICY</h3>

        <h4>23.1 Single Account Policy</h4>
        <p>Each user is permitted to operate only one TREXAR account.</p>
        <p>Creating, controlling, or operating multiple accounts, whether directly or indirectly, is strictly prohibited. This includes but is not limited to:</p>
        <ul>
          <li>Using different email addresses, phone numbers, or identities</li>
          <li>Creating accounts on behalf of others for personal benefit</li>
          <li>Using automated systems or scripts to generate accounts</li>
        </ul>
        <p>TREXAR reserves the right to determine, at its sole discretion, whether multiple accounts are linked to a single individual or entity.</p>

        <h4>23.2 Referral Program Integrity</h4>
        <p>The TREXAR referral program is designed to reward genuine user growth and participation.</p>
        <p>Users must not:</p>
        <ul>
          <li>Create fake or duplicate accounts to earn referral rewards</li>
          <li>Refer themselves, directly or indirectly</li>
          <li>Use misleading, deceptive, or spam methods to gain referrals</li>
          <li>Manipulate or exploit the referral system in any form</li>
        </ul>
        <p>Any activity deemed suspicious or abusive will be investigated.</p>

        <h4>23.3 Prohibited Activities</h4>
        <p>The following actions are strictly prohibited:</p>
        <ul>
          <li>Self-referrals using multiple accounts</li>
          <li>Coordinated referral abuse (group farming, bot activity)</li>
          <li>Use of stolen, fake, or unverifiable identities</li>
          <li>Exploiting system loopholes for financial gain</li>
        </ul>

        <h4>23.4 Enforcement & Penalties</h4>
        <p>If TREXAR determines that a user has violated this policy, we reserve the right to take one or more of the following actions without prior notice:</p>
        <ul>
          <li>Immediate account suspension or permanent freeze</li>
          <li>Forfeiture of all referral rewards and associated earnings</li>
          <li>Reversal of any benefits gained through abusive activity</li>
          <li>Restriction of withdrawals</li>
          <li>Termination of access to the Platform</li>
        </ul>
        <p>TREXAR's decision in such matters shall be final.</p>

        <h4>23.5 Investigation Rights</h4>
        <p>TREXAR may:</p>
        <ul>
          <li>Conduct internal investigations</li>
          <li>Request identity verification or supporting documentation</li>
          <li>Use automated systems and manual review to detect abuse</li>
        </ul>
        <p>Failure to comply with verification requests may result in account restriction.</p>

        <h4>23.6 No Liability</h4>
        <p>TREXAR shall not be liable for any loss of funds, rewards, or access resulting from enforcement of this policy.</p>

        <h4>23.7 Amendments</h4>
        <p>TREXAR reserves the right to modify or update this policy at any time. Continued use of the Platform constitutes acceptance of any changes.</p>
      </section>

      <section>
        <h3>24. CONTACT INFORMATION</h3>
        <p className="contact-email">
          📧 Email: <a href="mailto:support@trexarng.com">support&#64;trexarng.com</a>
        </p>
      </section>
    </div>
  );
};

export default TermsAndConditions;